// src/components/Lawyer/LawyerReportPDF.jsx
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logo2 from "../../assets/images/backgrounds/watermark-bg.png";
import { formatDateTime } from "../../utils/formatters";
const STUDENT_CODE = "ESTUDIANTE";

// ---------------------------
// Helpers
// ---------------------------
const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";
const L = (v) => (isBlank(v) ? "—" : String(v));

const joinComma = (...parts) =>
  parts
    .map((p) => (isBlank(p) ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");

const isStudent = (data) =>
  String(data?.academicLevelCode || "")
    .trim()
    .toUpperCase() === STUDENT_CODE;

const tutorLabel = (data) => {
  if (!data?.tutorLawyerId) return "—";
  const name = data?.tutorLawyerName;
  const email = data?.tutorLawyerEmail;
  if (!isBlank(name) && !isBlank(email)) return `${name} (${email})`;
  if (!isBlank(name)) return name;
  if (!isBlank(email)) return email;
  return String(data.tutorLawyerId);
};

// ---------------------------
// Styles
// ---------------------------
const styles = StyleSheet.create({
  page: { padding: 28, fontSize: 11, color: "#111" },

  watermarkWrap: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 0,
  },
  watermarkImg: {
    width: 420,
    opacity: 0.4,
    transform: "rotate(-18deg)",
  },

  content: { position: "relative", zIndex: 1 },

  h1: { fontSize: 18, marginBottom: 6, fontWeight: 700 },
  h2: {
    fontSize: 14,
    marginTop: 14,
    marginBottom: 6,
    fontWeight: 700,
    color: "#0b5ed7",
  },

  row: { flexDirection: "row", justifyContent: "space-between" },
  cell: { width: "49%", marginBottom: 6 },
  label: { color: "#666" },
  value: { marginTop: 2 },

  pill: {
    fontSize: 9,
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 10,
    backgroundColor: "#eef6ff",
    color: "#0b5ed7",
    marginRight: 6,
    marginBottom: 6,
  },

  section: { marginTop: 14, minPresenceAhead: 150 },

  tableCard: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderStyle: "solid",
    borderRadius: 6,
    overflow: "hidden",
  },

  trHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    borderStyle: "solid",
  },
  tr: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#f1f1f1",
    borderStyle: "solid",
  },
  trAlt: { backgroundColor: "#fafafa" },
  th: {
    paddingVertical: 6,
    paddingHorizontal: 6,
    fontWeight: 700,
    color: "#111827",
    fontSize: 9.5,
  },
  td: {
    flex: 1,
    paddingVertical: 5,
    paddingHorizontal: 4,
    fontSize: 9,
    lineHeight: 1.2,
  },

  header: {
    borderBottomWidth: 1,
    borderColor: "#ececec",
    paddingBottom: 8,
    marginBottom: 10,
  },
  status: {
    fontSize: 10,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 10,
    color: "#fff",
    alignSelf: "flex-start",
  },
  statusActive: { backgroundColor: "#198754" },
  statusInactive: { backgroundColor: "#dc3545" },

  footer: {
    position: "absolute",
    left: 28,
    right: 28,
    bottom: 18,
    fontSize: 9,
    color: "#666",
    flexDirection: "row",
    justifyContent: "space-between",
  },

  summaryCard: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderStyle: "solid",
    borderRadius: 6,
    backgroundColor: "#f9fafb",
    padding: 10,
  },
  summaryTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 6,
  },
  summaryText: {
    fontSize: 10,
    lineHeight: 1.35,
    color: "#111827",
    textAlign: "justify",
  },

  hint: { fontSize: 9, color: "#6b7280", marginTop: 3 },

  // ⭐ Banner estudiante
  bannerWrap: {
    marginTop: 8,
    marginBottom: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#a7f3d0",
    backgroundColor: "#ecfdf5",
  },
  bannerTitle: {
    fontSize: 12,
    fontWeight: 800,
    color: "#065f46",
    letterSpacing: 0.3,
  },
  bannerSubtitle: {
    marginTop: 3,
    fontSize: 9.5,
    color: "#065f46",
  },

  // ⭐ Card tutor
  tutorCard: {
    marginTop: 8,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#bfdbfe",
    backgroundColor: "#eff6ff",
    borderRadius: 8,
    padding: 10,
  },
  tutorTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  tutorTitle: { fontSize: 11, fontWeight: 800, color: "#1d4ed8" },
  tutorBadgeOk: {
    fontSize: 9,
    fontWeight: 700,
    color: "#065f46",
    backgroundColor: "#d1fae5",
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  tutorBadgeMissing: {
    fontSize: 9,
    fontWeight: 800,
    color: "#991b1b",
    backgroundColor: "#fee2e2",
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  tutorLineLabel: { fontSize: 9, color: "#1e3a8a" },
  tutorLineValue: {
    marginTop: 2,
    fontSize: 10.5,
    fontWeight: 700,
    color: "#0f172a",
  },
});

// ---------------------------
// Small components
// ---------------------------
const TwoCol = ({ leftLabel, leftValue, rightLabel, rightValue }) => (
  <View style={styles.row}>
    <View style={styles.cell}>
      <Text style={styles.label}>{leftLabel}</Text>
      <Text style={styles.value}>{leftValue}</Text>
    </View>
    <View style={styles.cell}>
      <Text style={styles.label}>{rightLabel}</Text>
      <Text style={styles.value}>{rightValue}</Text>
    </View>
  </View>
);

const TableCard = ({ columns, rows, emptyText = "—", wrap = true }) => {
  const hasRows = Array.isArray(rows) && rows.length > 0;
  if (!hasRows) return <Text>{emptyText}</Text>;

  return (
    <View style={styles.tableCard} wrap={wrap}>
      <View style={styles.trHeader}>
        {columns.map((c) => (
          <Text
            key={c.key}
            style={[
              styles.th,
              c.basis ? { flexBasis: c.basis, flexGrow: 0 } : { flex: 1 },
            ]}
          >
            {c.header}
          </Text>
        ))}
      </View>

      {rows.map((r, idx) => (
        <View
          key={r._key || idx}
          style={[styles.tr, idx % 2 === 1 && styles.trAlt]}
        >
          {columns.map((c) => (
            <Text
              key={c.key}
              style={[
                styles.td,
                c.basis ? { flexBasis: c.basis, flexGrow: 0 } : { flex: 1 },
              ]}
            >
              {L(r[c.key])}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
};

const KeyValueCard = ({ title, items }) => (
  <View style={styles.section} wrap={false}>
    <Text style={styles.h2}>{title}</Text>
    <View style={styles.tableCard} wrap>
      <View style={styles.trHeader}>
        <Text style={[styles.th, { flexBasis: "40%", flexGrow: 0 }]}>
          Campo
        </Text>
        <Text style={[styles.th, { flexBasis: "60%", flexGrow: 0 }]}>
          Valor
        </Text>
      </View>

      {(items || []).map((it, idx) => (
        <View
          key={it.key || idx}
          style={[styles.tr, idx % 2 === 1 && styles.trAlt]}
        >
          <Text style={[styles.td, { flexBasis: "40%", flexGrow: 0 }]}>
            {it.label}
          </Text>
          <Text style={[styles.td, { flexBasis: "60%", flexGrow: 0 }]}>
            {L(it.value)}
          </Text>
        </View>
      ))}
    </View>
  </View>
);

// ---------------------------
// Main
// ---------------------------
export default function LawyerReportPDF({ data }) {
  const today = (() => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  })();

  const student = isStudent(data);
  const hasTutor = !!data?.tutorLawyerId;

  const educationColumns = [
    { key: "TitleName", header: "Título", basis: "22%" },
    { key: "Institution", header: "Institución", basis: "22%" },
    { key: "AcademicLevelName", header: "Nivel", basis: "16%" },
    { key: "StartDate", header: "Inicio", basis: "13%" },
    { key: "GraduationDate", header: "Graduación", basis: "14%" },
    { key: "IsGraduated", header: "Graduado", basis: "13%" },
  ];

  const educationRows = (data?.educations ?? []).map((e, idx) => ({
    _key: e?.id || idx,
    TitleName: e?.titleName,
    Institution: e?.institution,
    AcademicLevelName: e?.academicLevelName,
    StartDate: formatDateTime(e?.startDate),
    GraduationDate: formatDateTime(e?.graduationDate),
    IsGraduated: e?.isGraduated ? "Sí" : "No",
  }));

  const attachmentColumns = [
    { key: "AttachmentTypeName", header: "Tipo", basis: "28%" },
    { key: "FileName", header: "Archivo" },
    { key: "Notes", header: "Notas", basis: "40%" },
  ];

  const attachmentRows = (data?.attachments ?? []).map((a, idx) => ({
    _key: a?.id || idx,
    AttachmentTypeName: a?.attachmentTypeName,
    FileName: a?.fileName,
    Notes: a?.notes,
  }));

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Marca de agua */}
        <View style={styles.watermarkWrap} fixed>
          <Image src={logo2} style={styles.watermarkImg} />
        </View>

        {/* Contenido */}
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.h1}>{L(data?.fullName)}</Text>

            {/* ⭐ Banner ESTUDIANTE (muy visible) */}
            {student && (
              <View style={styles.bannerWrap}>
                <Text style={styles.bannerTitle}>
                  ESTUDIANTE (Laboratorio jurídico)
                </Text>
                <Text style={styles.bannerSubtitle}>
                  En este perfil el tutor es obligatorio y la licencia
                  profesional puede no aplicar.
                </Text>
              </View>
            )}

            <View style={styles.row}>
              <View style={styles.cell}>
                <Text style={styles.label}>Correo</Text>
                <Text style={styles.value}>{L(data?.email)}</Text>

                {!isBlank(data?.companyName) && (
                  <Text style={styles.hint}>
                    Empresa: {L(data?.companyName)}
                  </Text>
                )}
              </View>

              <View style={styles.cell}>
                <Text style={styles.label}>Estado</Text>
                <Text
                  style={[
                    styles.value,
                    styles.status,
                    data?.isActive
                      ? styles.statusActive
                      : styles.statusInactive,
                  ]}
                >
                  {data?.isActive ? "Activo" : "Inactivo"}
                </Text>
              </View>
            </View>

            {/* Tutor destacado (solo si estudiante) */}
            {student && (
              <View style={styles.tutorCard}>
                <View style={styles.tutorTitleRow}>
                  <Text style={styles.tutorTitle}>Tutor asignado</Text>
                  <Text
                    style={
                      hasTutor ? styles.tutorBadgeOk : styles.tutorBadgeMissing
                    }
                  >
                    {hasTutor ? "OK" : "TUTOR PENDIENTE"}
                  </Text>
                </View>

                <Text style={styles.tutorLineLabel}>Tutor</Text>
                <Text style={styles.tutorLineValue}>{tutorLabel(data)}</Text>

                {!hasTutor && (
                  <Text style={styles.hint}>
                    No se encontró TutorLawyerId en el reporte. Verifica el
                    registro del abogado estudiante.
                  </Text>
                )}
              </View>
            )}
          </View>

          {/* Identidad */}
          <Text style={styles.h2}>Identidad</Text>
          <TwoCol
            leftLabel="Documento"
            leftValue={`${L(data?.documentTypeName)} ${L(
              data?.documentNumber
            )}`}
            rightLabel="Fecha de nacimiento"
            rightValue={formatDateTime(data?.birthDate)}
          />
          <TwoCol
            leftLabel="Género"
            leftValue={L(data?.genderName)}
            rightLabel="Teléfono móvil"
            rightValue={L(data?.phoneMobile)}
          />

          <TwoCol
            leftLabel="Teléfono alterno"
            leftValue={L(data?.phoneAlt)}
            rightLabel="Perfil"
            rightValue={student ? "Estudiante" : "Abogado"}
          />

          {/* Dirección */}
          <Text style={styles.h2}>Dirección</Text>
          <TwoCol
            leftLabel="Dirección"
            leftValue={joinComma(data?.addressLine1, data?.addressLine2) || "—"}
            rightLabel="Ubicación"
            rightValue={
              joinComma(
                data?.municipalityName,
                data?.departmentName,
                data?.countryName
              ) || "—"
            }
          />

          {/* Profesional */}
          <Text style={styles.h2}>Profesional</Text>
          <TwoCol
            leftLabel="Nivel académico"
            leftValue={L(data?.academicLevelName)}
            rightLabel="Código nivel"
            rightValue={L(data?.academicLevelCode)}
          />
          <TwoCol
            leftLabel="Título principal"
            leftValue={L(data?.mainDegreeTitle)}
            rightLabel="Institución principal"
            rightValue={L(data?.mainInstitution)}
          />

          {/* Graduación y Licencia: solo si NO estudiante */}
          {!student ? (
            <>
              <TwoCol
                leftLabel="Fecha graduación"
                leftValue={formatDateTime(data?.mainGraduationDate)}
                rightLabel="Licencia verificada"
                rightValue={data?.isLicenseVerified ? "Sí" : "No"}
              />
              <TwoCol
                leftLabel="Tarjeta / Licencia profesional"
                leftValue={L(data?.professionalLicenseNumber)}
                rightLabel="Expedido por"
                rightValue={L(data?.professionalLicenseIssuedBy)}
              />
              <TwoCol
                leftLabel="Fecha expedición"
                leftValue={formatDateTime(data?.professionalLicenseIssueDate)}
                rightLabel=" "
                rightValue=" "
              />
            </>
          ) : (
            <View style={{ marginTop: 6 }}>
              <Text style={styles.label}>Licencia profesional</Text>
              <Text style={styles.value}>No aplica (ESTUDIANTE)</Text>
            </View>
          )}

          {/* Especialidades */}
          <View style={{ marginTop: 10 }}>
            <Text style={styles.label}>Especialidades</Text>
            <View
              style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 4 }}
            >
              {(data?.specialtyNames ?? []).length ? (
                data.specialtyNames.map((s, i) => (
                  <Text key={`${s}-${i}`} style={styles.pill}>
                    {L(s)}
                  </Text>
                ))
              ) : (
                <Text>—</Text>
              )}
            </View>
          </View>

          {/* Resumen */}
          <Text style={styles.h2}>Resumen profesional</Text>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryTitle}>Descripción</Text>
            <Text style={styles.summaryText}>{L(data?.summary)}</Text>
          </View>

          {/* Roles */}
          <Text style={styles.h2}>Roles</Text>
          <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
            {(data?.roles ?? []).length ? (
              data.roles.map((r) => (
                <Text key={r.id} style={styles.pill}>
                  {L(r.name)}
                </Text>
              ))
            ) : (
              <Text>—</Text>
            )}
          </View>

          {/* Educación */}
          <Text style={styles.h2}>Formación académica</Text>
          <TableCard
            columns={educationColumns}
            rows={educationRows}
            emptyText="—"
            wrap
          />

          {/* Adjuntos */}
          <View style={styles.section} wrap={false}>
            <Text style={styles.h2}>Adjuntos</Text>
            <TableCard
              columns={attachmentColumns}
              rows={attachmentRows}
              emptyText="—"
              wrap={false}
            />
          </View>

          {/* Empresa */}
          <KeyValueCard
            title="Empresa"
            items={[
              { key: "companyName", label: "Nombre", value: data?.companyName },
              { key: "companyId", label: "ID empresa", value: data?.companyId },
            ]}
          />

          {/* Sistema */}
          <KeyValueCard
            title="Sistema"
            items={[
              { key: "lawyerId", label: "ID abogado", value: data?.id },
              {
                key: "securityUserId",
                label: "SecurityUserId",
                value: data?.securityUserId,
              },
              {
                key: "createdAt",
                label: "Creado",
                value: formatDateTime(data?.createdAt),
              },
              {
                key: "updatedAt",
                label: "Actualizado",
                value: formatDateTime(data?.updatedAt),
              },
            ]}
          />
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text>NovianCore • Reporte de Abogado • {today}</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `Página ${pageNumber} de ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
